import { objectToQueryString } from 'jsx/utils/queryStringHelper';
import { wrapUrls } from 'jsx/utils/urlWrapper';
import { encoder } from 'jsx/utils/queryParams';

const SIGNUP_URLS = {
  intro: () => '/signup/activation-intro',
};

const PHOTO_BOOTH_URLS = {
  frames: 'api.photoFrames',
} as const;

const MESSAGE_URLS = {
  post: 'api.patient.messages',
} as const;

const PHONE_NUMBER_URLS = {
  confirm: 'api.patient.phoneNumber.confirm',
  sendConfirmation: 'api.patient.phoneNumber.sendConfirmation',
  show: 'api.patient.phoneNumber',
} as const;

const API_URLS = {
  acceptCancelDiscount: 'api.patient.discounts.cancellation.acceptDiscount',
  activateUnbundling: 'app.patient.activate-unbundling',
  conversation: 'api.patient.conversations.entity',
  conversationCredits: 'api.patient.conversationCredits',
  conversations: 'api.patient.conversations',
  conversationsMarkRead: 'api.patient.conversations.entity.markRead',
  consultationExperience: 'api.patient.consultationExperiences.latest',
  consultationExperiences: 'api.patient.consultationExperiences',
  creditCard: 'app.patient.payment.creditCard',
  debugCloseConversation: 'api.patient.conversations.entity.close',
  getCommunicationPreferences: 'api.patient.communicationPreferences.email',
  messagesMarkRead: 'api.patient.messages.markRead',
  notification: 'api.patient.notifications.entity',
  notifications: 'api.patient.notifications',
  notificationsV2: 'api.v2.notifications',
  orders: 'api.ecommerce.orders',
  registerCard: 'app.patient.payment.registerCard',
  postcardFlags: 'api.patient.postcards.flags',
  postcards: 'api.patient.postcards',
  postcard: 'api.patient.postcards.entity',
  satisfactionResponse: 'api.patient.satisfactionResponse.entity',
  skinGoals: 'api.patient.skinGoals',
  smsPreferences: 'api.patient.smsPreferences',
  survey: 'api.surveys.entity',
  surveyResponses: 'api.surveys.responses',
  updateCommunicationPreferences: 'api.patient.communicationPreferences.update',
  routine: 'api.patient.routine',
} as const;

const MODULAR_CANCELLATION_URLS = {
  flowStart: () => '/dashboard/account/payment/cancellation' as const,
  beforeAndAfter: () =>
    '/dashboard/account/payment/cancellation/before-and-after' as const,
  cancel: () => '/dashboard/account/payment/cancellation/cancel' as const,
  cancelSuccess: () =>
    '/dashboard/account/payment/cancellation/cancel-success' as const,
  discountOffer: () =>
    '/dashboard/account/payment/cancellation/discount-offer' as const,
  discountSuccess: () =>
    '/dashboard/account/payment/cancellation/discount-success' as const,
  downgrade: () => '/dashboard/account/payment/cancellation/downgrade' as const,
  messageProvider: () =>
    '/dashboard/account/payment/cancellation/message-provider' as const,
  pause: () => '/dashboard/account/payment/cancellation/pause' as const,
  pauseSuccess: () =>
    '/dashboard/account/payment/cancellation/pause-success' as const,
  skipRx: () => '/dashboard/account/payment/cancellation/skip-rx' as const,
  skipRxConfirmation: () =>
    '/dashboard/account/payment/cancellation/skip-rx-confirmation' as const,
  skipShipment: () =>
    '/dashboard/account/payment/cancellation/skip-shipment' as const,
  skipShipmentSuccess: () =>
    '/dashboard/account/payment/cancellation/skip-shipment-success' as const,
  unpause: () => '/dashboard/account/payment/cancellation/unpause' as const,
  unpauseSuccess: () =>
    '/dashboard/account/payment/cancellation/unpause-success' as const,
} as const;

const RESUBSCRIBE_BASE = '/dashboard/resubscribe' as const;

const RESUBSCRIBE_URLS = {
  base: () => RESUBSCRIBE_BASE,
  resubscriptionDone: () => `${RESUBSCRIBE_BASE}/done` as const,
  resubscriptionPayment: () => `${RESUBSCRIBE_BASE}/payment` as const,
  resubscriptionPhotos: () => `${RESUBSCRIBE_BASE}/photos` as const,
  resubscriptionCheckout: () => `${RESUBSCRIBE_BASE}/checkout` as const,
  resubscriptionConsultation: () => `${RESUBSCRIBE_BASE}/consultation` as const,
  resubscriptionQuestion: (questionId: number | string) =>
    `${RESUBSCRIBE_BASE}/${questionId}` as const,
} as const;

/**
 * TODO-Michael-Altamirano: move all non-account details URLS
 * out of dashboard/account namespace.
 */
const ACCOUNT_DETAILS_URLS = {
  base: () => '/dashboard/account',
  communicationPreferences: () =>
    '/dashboard/account/communication-preferences',
  smsPreferences: () => '/dashboard/account/communication-preferences/sms',
  accountCredits: () => '/dashboard/account/credits',
  contactDetails: () => '/dashboard/account/my-info',
  loginDetails: () => '/dashboard/account/login-details',
  pharmacy: () => '/dashboard/account/pharmacy',
} as const;

const MESSAGES_BASE = '/dashboard/messages' as const;

const MESSAGES_URLS = {
  base: () => MESSAGES_BASE,
  sendMessage: () => `${MESSAGES_BASE}/send-message` as const,
  purchaseCredits: () => `${MESSAGES_BASE}/purchase-credits` as const,
};

const MESSAGES_REDIRECT_URLS = {
  purchaseCredits: {
    deprecated: () => '/dashboard/purchase-credits' as const,
    redirect: MESSAGES_URLS.purchaseCredits,
  },
  questionnaire: {
    deprecated: () => '/dashboard/treatment/questionnaire' as const,
    redirect: MESSAGES_URLS.sendMessage,
  },
  sendMessage: {
    deprecated: () => '/dashboard/send-message' as const,
    redirect: MESSAGES_URLS.sendMessage,
  },
} as const;

/**
 * We maintain these URLs solely as redirects for old functionality
 */
const DEPRECATED_URLS = {
  accountFormulation: () => '/dashboard/account/formulation',
  communicationPreferences: () => '/dashboard/communication-preferences',
  plan: () => '/dashboard/account/plan',
  messages: MESSAGES_REDIRECT_URLS,
} as const;

const SURVEYS_URLS = {
  base: () => `/dashboard/surveys`,
  survey: (slug: string) => `/dashboard/surveys/${slug}`,
  surveyV2: (slug: string) => `/dashboard/surveyV2/${slug}`,
  surveyDone: (slug: string) => `/dashboard/surveys/${slug}/screens/done`,
  surveyScreens: (slug: string, screenId: number | string) =>
    `/dashboard/surveys/${slug}/screens/${screenId}`,
} as const;

export const DASHBOARD_URLS = {
  // Nav Bar Endpoints
  home: () => '/dashboard/home',
  community: () => '/dashboard/community',
  dashboard: () => '/dashboard',
  messages: MESSAGES_URLS,
  progress: () => '/dashboard/progress',
  treatment: () => '/dashboard/treatment',
  treatmentPlan: () => '/dashboard/treatment-plan',
  rewards: () => '/dashboard/rewards',

  // Auth Endpoints
  login: () => '/auth/login',

  // Payment Endpoints
  payment: () => '/dashboard/account/payment',
  paymentMethodUpdate: () => '/dashboard/account/payment/update',

  // Cancellation routes
  modularCancellation: MODULAR_CANCELLATION_URLS,

  account: ACCOUNT_DETAILS_URLS,

  DEPRECATED: DEPRECATED_URLS,

  // Resubscription Endpoints
  resubscribe: RESUBSCRIBE_URLS,

  // Survey Endpoints
  surveys: SURVEYS_URLS,

  // Signup Endpoints
  signup: SIGNUP_URLS,

  surveyV2base: () => `/dashboard/surveyV2`,
  surveyV2: (slug: string) => `/dashboard/surveyV2/${slug}`,
  surveyV2Outcomes: (index: number | string, surveyType?: string | null) => {
    return surveyType
      ? `/dashboard/surveyV2/${surveyType}/outcomes/${index}`
      : `/dashboard/surveyV2/outcomes/${index}`;
  },

  // General Dashboard Endpoints
  confirmEmail: () => '/dashboard/confirm-email',
  /**
   * TODO-MichaelA: We redirect to `/messages` when there is no ID.
   * We should make this behavior more explicit/differentiated.
   */
  consultations: (id?: number | string) =>
    `/dashboard/consultations${id ? `/${id}` : ''}`,
  feedback: (id: number | string) => `/dashboard/feedback/${id}`,
  feedbackDone: () => `/dashboard/feedback/done`,
  manageSubscription: () => '/dashboard/manage-subscription',
  customizeSubscription: (
    options: {
      preselect?: Array<number>;
      useReturnNavigation?: boolean;
    } = {},
  ) => {
    const qs = objectToQueryString(options);
    const params = qs !== '' ? `?${qs}` : '';
    return `/dashboard/manage-subscription/customize${params}`;
  },
  messageProvider: () => '/dashboard/message-provider',
  more: () => '/dashboard/more',
  notifications: () => '/dashboard/notifications',
  currentConsultation: () => '/dashboard/consultations?tab=active',
  previousConsultations: () => '/dashboard/consultations?tab=previous',
  preconsultTreatment: () => '/dashboard/welcome',
  requestMedicalRecord: () => '/dashboard/request-medical-record',
  routine: () => '/dashboard/routine',
  shipments: (openRescheduler?: boolean) => {
    const params = encoder.encode({
      openRescheduler,
    });

    return `/dashboard/shipments${params ? `?${params}` : ''}`;
  },
  peiIngredients: () => '/dashboard/pei-ingredients',
  homeWithPeiIngredientsModal: () =>
    '/dashboard/home?showPeiIngredientsModal=true',

  // Legacy Endpoints (used for redirects only)
  journey: () => '/dashboard/journey',

  // API Endpoints
  // TODO - Refactor API endpoints to use Ziggy
  apiConsultations: () => '/api/consultations',
  cohorts: (id: number | null = null) =>
    `/api/patient/cohorts${id ? `/${id}` : ''}`,
  contactDetailsApi: () => '/app/users/self/contactDetails',
  postcards: (id: number | null = null) =>
    `/api/postcards${id ? `/${id}` : ''}`,
  postcardFlags: (id: number | null = null) =>
    `/api/postcard-flags${id ? `/${id}` : ''}`,
  postcardLikes: (id: number | null = null) =>
    `/api/postcard-likes${id ? `/${id}` : ''}`,
  pregnancyNote: () => '/api/patient/pregnancy-note',
  resendEmailConfirmation: () => '/api/patient/resend-confirmation-email',
  updateEmergencyContact: () => '/api/patient/emergencyContact',
  userPharmacy: () => '/app/users/self/pharmacy',

  api: {
    ...wrapUrls(API_URLS),
    messages: wrapUrls(MESSAGE_URLS),
    phoneNumber: wrapUrls(PHONE_NUMBER_URLS),
    photoBooth: wrapUrls(PHOTO_BOOTH_URLS),
  } as const,
} as const;
